import React from 'react';

import _get from 'lodash/get';
import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import type { Frequency } from 'app-components/flux/scheduled-transactions/frequency';

import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';

import CustomFrequency from 'components/CustomFrequency/index';
import SDatePicker from 'components/SDatePicker';

import { useScheduledTransactionEditFormAccessors } from '../hooks/useScheduledTransactionEditFormFields';
import type { ScheduledTransactionEditFormAccessors } from '../types';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '500px',
    width: '100%',
  },
  rightSide: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    width: '100%',
    gap: theme.spacing(3),
    '> div:last-of-type': {
      paddingRight: 0,
      '&.MuiButtonBase-root': {
        margin: 0,
      },
    },
  },
  dateWidth: {
    width: '100%',
    '& .MuiFormLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: 14,
    },
  },
  flex: {
    display: 'flex',
  },
  underline: {
    textDecoration: 'underline',
  },
  selectMenuItem: {
    borderRadius: '8px 8px 0px 0px !important',
    '&:hover': {
      borderRadius: '8px !important',
    },
  },
  selectedMenuItem: {
    borderRadius: 8,
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  formControl: {
    width: '100%',
    '.MuiFilledInput-root': {
      backgroundColor: theme.palette.background.default,
    },
  },
}));

interface FrequencyTabProps {
  form: ScheduledTransactionEditFormAccessors;
  minimumDate?: DateTime;
}

function FrequencyTab({ form: formAccessors, minimumDate }: FrequencyTabProps) {
  const { classes } = useStyles();
  const form = useScheduledTransactionEditFormAccessors(formAccessors);

  const { values } = form;
  const { errors } = form.validation;

  const { dueOn, virtualEndOn, virtualFrequency, transaction } = values || {};

  return (
    <div className={classes.root}>
      <div className={classes.rightSide}>
        <FormControl variant='filled' className={classes.formControl}>
          <InputLabel className={classes.inputLabel}>Select Frequency</InputLabel>
          <Select
            id='frequency-select'
            value={virtualFrequency}
            onChange={(event) => {
              form.setters.setFieldValue('virtualFrequency', event.target.value);
              if (event.target.value === 'One-time payment') {
                form.setters.setFieldValue('virtualEndOn', undefined);
              }
            }}
          >
            {customFrequencyUtils.frequencyAliases.map((item) => {
              const isSelected = virtualFrequency === item;
              const splitName = item.includes('X') ? item.split(' ') : item;
              const needDivider =
                item === 'One-time payment' ||
                item === 'Every year' ||
                item === 'Every quarter' ||
                item === 'Every X years';
              return (
                <MenuItem
                  key={item}
                  value={item}
                  divider={needDivider}
                  sx={{ borderRadius: '8px 8px 0px 0px' }}
                  className={isSelected ? classes.selectedMenuItem : classes.selectMenuItem}
                >
                  {item.includes('X') ? (
                    <Typography className={classes.flex}>
                      {splitName[0]}&nbsp;<span className={classes.underline}> {splitName[1]} </span>&nbsp;
                      {splitName[2]}
                    </Typography>
                  ) : (
                    item
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Divider />

        {virtualFrequency !== 'One-time payment' && (
          <div className={classes.row}>
            {/* Date Field */}
            <SDatePicker
              // @ts-expect-error FIXME TS2322
              id='next-date'
              value={dueOn}
              onChange={(value: DateTime): void => {
                const updatedValue = value?.isValid ? value : undefined;
                form.setters.setFieldValue('dueOn', updatedValue);
              }}
              onError={(startError: string): void => {
                form.validation.setFieldError('dueOn', startError);
              }}
              label='Next Due Date'
              keyboardShortcutsEnabled={false}
              className={classes.dateWidth}
              error={Boolean(_get(errors, 'dueOn'))}
              helperText={_get(errors, 'dueOn')}
              minDate={minimumDate}
              maxDate={DateTime.local(2100, 1, 1)}
            />

            {/* End Date Field */}
            <SDatePicker
              // @ts-expect-error FIXME TS2322
              id='end-date'
              value={virtualEndOn}
              onChange={(newDate: DateTime): void => {
                form.setters.setFieldValue('virtualEndOn', newDate);
              }}
              onError={(endError: string): void => {
                form.validation.setFieldError('virtualEndOn', endError);
              }}
              label='End Date'
              className={classes.dateWidth}
              error={Boolean(_get(errors, 'virtualEndOn'))}
              helperText={_get(errors, 'virtualEndOn')}
              minDate={dueOn || DateTime.local(1900, 1, 1)}
              maxDate={DateTime.local(2100, 1, 1)}
              clearable
            />
          </div>
        )}

        {/* Custom Frequency specification */}
        <div className={classes.row}>
          <CustomFrequency
            initialState={
              {
                ...values.virtualRecurrence,
                alias: virtualFrequency,
                dueOn: dueOn?.startOf('day'),
                endOn: virtualEndOn?.startOf('day'),
                frequency: customFrequencyUtils.getFrequencyFromAlias(virtualFrequency) as Frequency,
              } satisfies RecurrenceType
            }
            currentDueOn={dueOn?.startOf('day')}
            currentUntil={virtualEndOn?.startOf('day')}
            currentAlias={virtualFrequency}
            currentFrequency={customFrequencyUtils.getFrequencyFromAlias(virtualFrequency) as Frequency}
            accountId={transaction.accountId}
            amount={transaction.amount}
            error={Object.keys(errors).length > 0}
            onChange={(virtualRecurrence) => {
              setTimeout(() => {
                form.setters.setFieldValue('virtualRecurrence', virtualRecurrence);
              }, 0);
            }}
            onDateChange={(date: DateTime) => {
              setTimeout(() => {
                form.setters.setFieldValue('dueOn', date);
              }, 0);
            }}
          />
        </div>
      </div>
    </div>
  );
}

FrequencyTab.displayName = 'FrequencyTab';

export default FrequencyTab;
